import { type RefObject, useCallback, useEffect, useState } from "react";

import { useDebounce } from "../../hooks/use-debounce";
import { useResizeObserver } from "../../hooks/use-resize-observer";

export interface ScrollSizes {
  contentSize: number;
  viewportSize: number;
}

export const useScrollSizes = (
  viewportRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  isHorizontal: boolean,
) => {
  const measureSizes = useCallback((): ScrollSizes => {
    if (!viewportRef.current) {
      return { viewportSize: 0, contentSize: 0 };
    }

    const viewport = viewportRef.current;

    return {
      viewportSize: isHorizontal ? (viewport.clientWidth ?? 0) : (viewport.clientHeight ?? 0),
      contentSize: isHorizontal ? (viewport.scrollWidth ?? 0) : (viewport.scrollHeight ?? 0),
    };
  }, [viewportRef, isHorizontal]);

  const [sizes, setSizes] = useState<ScrollSizes>(() => measureSizes());

  const handleResize = useDebounce(
    useCallback(() => {
      setSizes(measureSizes());
    }, [measureSizes]),
    16,
  );

  useResizeObserver(viewportRef.current, handleResize);
  useResizeObserver(contentRef.current, handleResize);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  return sizes;
};
